import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Plus, ExternalLink, Download, Mail, AlertCircle, BadgeCheck, Play } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { NUEVO_INGRESO, CONVOCATORIAS } from "../data/content";

export const NuevoIngreso = () => {
  const [convo, setConvo] = useState(null);
  const [playVideo, setPlayVideo] = useState(false);

  return (
    <section id="nuevo-ingreso" data-testid="nuevo-ingreso-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-5">
            {NUEVO_INGRESO.overline}
          </p>
          <h2 className="font-serif-display text-3xl md:text-5xl font-semibold text-[#2D1B4E] tracking-tight leading-[1.1]">
            {NUEVO_INGRESO.title}
          </h2>
          <p className="mt-5 text-base text-[#5F5670] leading-relaxed">
            {NUEVO_INGRESO.intro}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
            {NUEVO_INGRESO.steps.map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                data-testid={`ingreso-step-${i}`}
                className="border border-[#E8E4EE] rounded-sm p-6 hover:border-[#5B3E96]/40 transition-colors"
              >
                <span className="font-serif-display text-4xl font-semibold text-[#5B3E96]/40">
                  {s.n}
                </span>
                <h3 className="font-serif-display text-xl font-semibold text-[#2D1B4E] mt-2">
                  {s.title}
                </h3>
                <p className="text-sm text-[#5F5670] mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>

          <div data-testid="ingreso-requirements" className="bg-[#2D1B4E] text-white rounded-sm p-8 h-fit">
            <h3 className="font-serif-display text-2xl font-semibold">
              Documentos requeridos
            </h3>
            <ul className="mt-6 space-y-4">
              {NUEVO_INGRESO.requirements.map((r) => (
                <li key={r} className="flex items-start gap-3 text-sm text-white/85">
                  <CheckCircle2 strokeWidth={1.5} className="w-5 h-5 text-[#C7B8E6] shrink-0 mt-0.5" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Convocatorias por carrera */}
        <div className="mt-20">
          <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-3">
            {CONVOCATORIAS.overline}
          </p>
          <h3 className="font-serif-display text-2xl md:text-3xl font-semibold text-[#2D1B4E] tracking-tight">
            {CONVOCATORIAS.title}
          </h3>
          <p className="mt-3 text-base text-[#5F5670] leading-relaxed max-w-2xl">
            {CONVOCATORIAS.intro}
          </p>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CONVOCATORIAS.items.map((c, i) => {
              const Icon = c.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  data-testid={`convocatoria-card-${i}`}
                  className="bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm p-7 flex flex-col hover:-translate-y-1 transition-transform duration-300"
                >
                  <span className="flex items-center justify-center w-12 h-12 bg-white border border-[#E8E4EE] rounded-sm">
                    <Icon strokeWidth={1.5} className="w-6 h-6 text-[#5B3E96]" />
                  </span>
                  <p className="text-[10px] uppercase tracking-overline text-[#5B3E96] font-semibold mt-4">
                    {c.degree}
                  </p>
                  <h4 className="font-serif-display text-xl font-semibold text-[#2D1B4E] mt-1">
                    {c.career}
                  </h4>
                  <p className="text-sm text-[#5F5670] mt-2 leading-relaxed">
                    Convocatoria {c.period}
                  </p>
                  <button
                    type="button"
                    onClick={() => setConvo(c)}
                    data-testid={`convocatoria-vermas-${i}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-medium self-start rounded-sm px-4 py-2 bg-[#2D1B4E] text-white hover:bg-[#1A1523] transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Ver convocatoria
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Video: Cómo llegar a Servicios Escolares */}
        <div className="mt-20" data-testid="ingreso-video-block">
          <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mb-3">
            Video guía
          </p>
          <h3 className="font-serif-display text-2xl md:text-3xl font-semibold text-[#2D1B4E] tracking-tight">
            Cómo llegar a Servicios Escolares
          </h3>
          <p className="mt-3 text-base text-[#5F5670] leading-relaxed max-w-2xl">
            Mira este video para ubicar fácilmente el Departamento de Servicios Escolares el día de tu inscripción.
          </p>
          <div className="mt-8 flex justify-center">
            <div className="relative w-full max-w-sm aspect-[478/850] bg-[#1A1523] rounded-sm overflow-hidden border border-[#E8E4EE] shadow-lg">
              {playVideo ? (
                <video
                  src="/videos/como-llegar-servicios-escolares.mp4"
                  controls
                  autoPlay
                  playsInline
                  data-testid="ingreso-video"
                  className="w-full h-full object-contain bg-black"
                >
                  Tu navegador no soporta la reproducción de video.
                </video>
              ) : (
                <button
                  type="button"
                  onClick={() => setPlayVideo(true)}
                  data-testid="ingreso-video-play"
                  aria-label="Reproducir video: Cómo llegar a Servicios Escolares"
                  className="group absolute inset-0 w-full h-full"
                >
                  <img
                    src="/videos/como-llegar-poster.jpg"
                    alt="Portada del video: Cómo llegar a Servicios Escolares"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <span className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors" />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-20 h-20 rounded-full bg-white/90 text-[#2D1B4E] shadow-xl group-hover:scale-110 transition-transform">
                    <Play strokeWidth={2} className="w-8 h-8 translate-x-0.5" fill="currentColor" />
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!convo} onOpenChange={(o) => !o && setConvo(null)}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="convocatoria-modal">
          <DialogHeader>
            <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold text-left">
              {convo?.degree} · {convo?.period}
            </p>
            <DialogTitle className="font-serif-display text-2xl text-[#2D1B4E] text-left">
              {convo?.career}
            </DialogTitle>
          </DialogHeader>

          {convo && (
            <div className="mt-1">
              {/* Requisitos previos */}
              <div className="bg-[#EDE8F5] border border-[#D8CCEC] rounded-sm p-5">
                <p className="text-sm font-semibold text-[#2D1B4E] flex items-center gap-2 mb-3">
                  <BadgeCheck className="w-4 h-4 text-[#5B3E96]" /> Antes de comenzar, verifica que cumples con:
                </p>
                <ul className="space-y-2">
                  {convo.prerequisitos.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1A1523] leading-relaxed">
                      <CheckCircle2 className="w-4 h-4 text-[#5B3E96] shrink-0 mt-0.5" /> {r}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Pasos */}
              <p className="text-xs uppercase tracking-overline text-[#5B3E96] font-semibold mt-8 mb-4">
                Proceso paso a paso
              </p>
              <ol className="space-y-6">
                {convo.pasos.map((p, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#2D1B4E] text-white text-sm font-semibold shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#2D1B4E]">{p.title}</p>
                      <p className="text-sm text-[#5F5670] mt-1 leading-relaxed">{p.desc}</p>
                      {p.list && (
                        <ul className="mt-3 space-y-1.5">
                          {p.list.map((it, li) => (
                            <li key={li} className="flex items-start gap-2 text-sm text-[#1A1523] leading-relaxed">
                              <span className="text-[#5B3E96] mt-1">•</span> {it}
                            </li>
                          ))}
                        </ul>
                      )}
                      {(p.links || (p.link ? [p.link] : [])).length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {(p.links || [p.link]).map((lk, li) => (
                            <a
                              key={li}
                              href={lk.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`convocatoria-step-link-${i}-${li}`}
                              className="inline-flex items-center gap-2 text-sm font-medium rounded-sm px-3 py-2 bg-white border border-[#D8CCEC] text-[#5B3E96] hover:bg-[#EDE8F5] transition-colors break-all"
                            >
                              <ExternalLink className="w-4 h-4 shrink-0" /> {lk.label}
                            </a>
                          ))}
                        </div>
                      )}
                      {p.note && (
                        <div className="mt-3 bg-[#FBF7E9] border border-[#EADFAE] rounded-sm p-3 flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-[#7A5B12] shrink-0 mt-0.5" />
                          <p className="text-sm text-[#5C4A15] leading-relaxed">
                            <span className="font-semibold">Nota:</span> {p.note}
                          </p>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>

              {/* Contacto */}
              {convo.contactEmail && (
                <a
                  href={`mailto:${convo.contactEmail}`}
                  data-testid="convocatoria-email"
                  className="mt-7 flex items-center gap-3 bg-[#F4F2F7] border border-[#E8E4EE] rounded-sm px-4 py-3 hover:bg-[#EDE8F5] transition-colors"
                >
                  <Mail className="w-5 h-5 text-[#5B3E96] shrink-0" />
                  <div>
                    <p className="text-[10px] uppercase tracking-overline text-[#5F5670]">Contacto de admisión</p>
                    <p className="text-sm font-medium text-[#5B3E96] break-all">{convo.contactEmail}</p>
                  </div>
                </a>
              )}

              {/* Cierre */}
              {convo.closing && (
                <p className="mt-6 text-sm text-[#5F5670] leading-relaxed border-l-2 border-[#5B3E96] pl-4">
                  {convo.closing}
                </p>
              )}

              {convo.startNote && (
                <p className="mt-4 font-serif-display text-lg text-[#5B3E96]">{convo.startNote}</p>
              )}

              {/* Descargar PDF */}
              <a
                href={convo.pdf}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="convocatoria-download"
                className="mt-7 flex items-center justify-between gap-3 bg-[#2D1B4E] text-white rounded-sm px-5 py-4 hover:bg-[#1A1523] transition-colors group"
              >
                <span className="text-sm font-medium">Descargar convocatoria completa (PDF)</span>
                <Download className="w-5 h-5 shrink-0 group-hover:translate-y-0.5 transition-transform" />
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
